import React, { useRef, useEffect, useState } from "react";
import "./otp-input.scss";

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  label?: string;
  labelAddon?: React.ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  requiredError?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: (value: string) => void;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  className?: string;
  inputClassName?: string;
}

let idCounter = 0;

/**
 * OtpInput - A reusable multi-box OTP/PIN verification code input component.
 * Part of the @risalabs_frontend_org/oasis-ui-kit library.
 */
export const OtpInput = ({
  value = "",
  onChange,
  length = 6,
  label,
  labelAddon,
  hint,
  error,
  required,
  requiredError = "This field is required",
  disabled = false,
  autoFocus = false,
  onComplete,
  size = "md",
  fullWidth = false,
  className,
  inputClassName,
}: OtpInputProps) => {
  const [fallbackId] = useState(() => `otp-input-${++idCounter}`);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const cls = "otp-input";

  // Ensure refs array has the right size
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Handle autoFocus on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Construct individual character array from value prop
  const items = Array.from({ length }, (_, i) => value[i] || "");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const val = e.target.value;
    // We only accept numeric digits
    const cleanedVal = val.replace(/\D/g, "");

    if (!cleanedVal) {
      // If cleared, update value and do not auto-advance
      const newItems = [...items];
      newItems[index] = "";
      const newValue = newItems.join("");
      onChange(newValue);
      return;
    }

    // Take only the last entered character if multiple (e.g. typing over)
    const singleDigit = cleanedVal[cleanedVal.length - 1] || "";
    const newItems = [...items];
    newItems[index] = singleDigit;
    const newValue = newItems.join("");

    onChange(newValue);

    // Auto-advance if not the last field
    if (singleDigit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Trigger onComplete if filled
    if (newValue.length === length && onComplete) {
      onComplete(newValue);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      // If the current input is empty, move focus to the previous one
      if (!items[index] && index > 0) {
        const newItems = [...items];
        newItems[index - 1] = "";
        const newValue = newItems.join("");
        onChange(newValue);
        inputRefs.current[index - 1]?.focus();
        e.preventDefault();
      } else if (items[index]) {
        // If current is filled, clear it but keep focus
        const newItems = [...items];
        newItems[index] = "";
        const newValue = newItems.join("");
        onChange(newValue);
        e.preventDefault();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData.getData("text").trim();
    // Only accept numeric digits
    const digits = pastedData.replace(/\D/g, "").slice(0, length);

    if (digits) {
      onChange(digits);

      // Focus the last input box or the next unfilled one
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();

      if (digits.length === length && onComplete) {
        onComplete(digits);
      }
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    setTouched(true);
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  const isEmpty = value.trim() === "";
  const requiredValidationError = required && touched && isEmpty ? requiredError : undefined;
  const resolvedError = error || requiredValidationError;
  const hasError = !!resolvedError;

  const rootClass = [
    cls,
    `${cls}--${size}`,
    fullWidth ? `${cls}--full-width` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      {(label || labelAddon) && (
        <div className={`${cls}__label-row`}>
          {label && (
            <label className={`${cls}__label`}>
              {label}
              {required && <span className={`${cls}__required`}> *</span>}
            </label>
          )}
          {labelAddon && <span className={`${cls}__label-addon`}>{labelAddon}</span>}
        </div>
      )}

      <div className={`${cls}__field-group`}>
        {items.map((char, index) => {
          const isFocused = focusedIndex === index;
          const fieldClass = [
            `${cls}__field`,
            isFocused ? `${cls}__field--focused` : "",
            hasError ? `${cls}__field--error` : "",
            disabled ? `${cls}__field--disabled` : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={index} className={fieldClass}>
              <input
                ref={(el) => {
                  if (el) inputRefs.current[index] = el;
                }}
                id={`${fallbackId}-${index}`}
                className={[`${cls}__input`, inputClassName]
                  .filter(Boolean)
                  .join(" ")}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={char}
                disabled={disabled}
                onChange={(e) => handleInputChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined} // paste handler primarily on first input box
                onFocus={() => handleFocus(index)}
                onBlur={handleBlur}
                autoComplete={index === 0 ? "one-time-code" : "off"}
              />
            </div>
          );
        })}
      </div>

      {resolvedError && <div className={`${cls}__error`}>{resolvedError}</div>}
      {!resolvedError && hint && <div className={`${cls}__hint`}>{hint}</div>}
    </div>
  );
};

export default OtpInput;
